/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getAccountSettingsService } from 'src/core/services/accountSettingsService';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';

export type AccountSettingsHandlerGroup = ReturnType<typeof createAccountSettingsHandlerGroup>;

/**
 * Creates a handler group of functions for account settings to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createAccountSettingsHandlerGroup = (_: HandlerGroupFactoryDeps) => {
    /**
     * Fetches the feature availability settings
     * as object that its keys represents the features' names and
     * the value is a boolean to indicate whether the feature is available or not.
     */
    const fetchFeaturesSettingsPerAzureCsAccount = async ({ csAccountStore }: HandlerParams<{ csAccountStore: AzureCsAccountStore }>) => {
        const tracker = csAccountStore.featureSettingsTracker;

        if (tracker.isRunning || tracker.isCompleted) {
            return;
        }

        tracker.setState('running');

        try {
            const featureSettingsSwagger = await getAccountSettingsService().getFeaturesSettings();
            csAccountStore.setFeaturesSettingsObject(featureSettingsSwagger);
            tracker.setState('completed');
        } catch (e) {
            tracker.setState('failed');
        }
    };

    return { fetchFeaturesSettingsPerAzureCsAccount };
};
